/**
 * Created by ldiaz on 27/02/18.
 */

import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Router } from '@angular/router';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import { environment } from '../../../../../environments/environment';
import * as CryptoJS from 'crypto-js';
import {catchError, filter, map, switchMap, take} from 'rxjs/operators';
import { EncryptService } from '../../../shared/services/encrypt.service';
import { AuthModel } from '../../models/auth.model';
import { AuthService } from '../auth.service';
import { AuthHTTPService } from './auth-http.service';

const HASH_KEY = CryptoJS.enc.Utf8.parse(environment.HASH_KEY.trim());
const HASH_IV = CryptoJS.enc.Utf8.parse(environment.HASH_IV);
const AUTH_LOCAL_STORAGE_KEY = `${environment.appVersion}-${environment.USERDATA_KEY}`;

@Injectable()
export class AuthHttpClientInterceptor implements HttpInterceptor {
  private isRefreshing = false; // Flag para controlar el estado de refresh
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  constructor(
    private encryptService: EncryptService,
    private injector: Injector,
    private router: Router
  ) {}
  private setAuthFromLocalStorage(auth: AuthModel): boolean {
    // store auth authToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
    if (auth && auth.authToken) {
      localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, this.encryptService.encrypt(JSON.stringify(auth)));
      return true;
    }
    return false;
  }
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let globalHeaders: HttpHeaders = req.headers;
    // Configurar encabezados básicos
    globalHeaders = globalHeaders.append('Content-Type', 'application/json');
    if (globalHeaders.has('x-app-key')) {
      globalHeaders = globalHeaders.set('x-app-key', environment.appKey);
    } else {
      globalHeaders = globalHeaders.append('x-app-key', environment.appKey);
    }

    if (!environment.dataEncrypted) {
      globalHeaders = globalHeaders.append('x-encrypted', 'false');
    }
    globalHeaders = globalHeaders.append('x-web', 'true');

    if (req.url.indexOf("refresh") > -1) {
      req = req.clone({ headers: globalHeaders });
      return next.handle(req);
    }

    // Agregar token de autorización (excepto en rutas públicas)
    if (!req.url.includes('login') && !req.url.includes('get_email_invitation') && !req.url.includes('generate_join_request')) {
      const authUser = this.getAuthFromLocalStorage();
      if (authUser) {
        globalHeaders = globalHeaders.set('Authorization', `Bearer ${this.getSessionKey("token")}`);
      }

    }


    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(req.method) && !req.url.includes("login")&& !req.url.includes("logout") &&!req.url.includes("verification_code")) {
      const csrfToken =  this.getSessionKey("csrf-token") || '';
      if (csrfToken) {
          globalHeaders = globalHeaders.set('x-csrftoken', csrfToken);
      }
    }

    req = req.clone({ headers: globalHeaders });

    return next.handle(req).pipe(
      catchError((err) => {
        // Las rutas públicas (activación, join request) manejan su propio error;
        // no deben disparar el refresh/redirect global del interceptor.
        const isPublicRequest = req.url.includes('eager_activation') || req.url.includes('generate_join_request');
        if (err.status === 401 && !isPublicRequest) {
          return this.handle401Error(req, next);
        }

        console.error('Error en la solicitud:', err);
        return throwError(() => err);
      })
    );
  }
  getSessionKey(key: string): string | null {
    return sessionStorage.getItem(key);
    /*const urlSafeValue = sessionStorage.getItem(key);
    if (!urlSafeValue) {
      return null;
    }

    // Convertir de formato URL-Safe a Base64 estándar
    const base64Value = this.base64UrlDecode(urlSafeValue);

    // Desencriptar el valor
    const decryptedBytes = CryptoJS.AES.decrypt(base64Value, environment.refreshTokenSecret);
    return decryptedBytes.toString(CryptoJS.enc.Utf8);*/
  }

// Método para decodificar Base64 desde formato URL-Safe
  private base64UrlDecode(base64UrlString: string): string {
    let base64String = base64UrlString.replace(/-/g, '+').replace(/_/g, '/');
    while (base64String.length % 4 !== 0) {
      base64String += '=';
    }
    return base64String;
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const  authService:AuthService = this.injector.get(AuthService);
    const  authHttpService:AuthHTTPService = this.injector.get(AuthHTTPService);
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = authService.getSessionKey("refreshToken");
      if (refreshToken) {
        return authHttpService.refreshToken(refreshToken).pipe(
          switchMap((res) => {
            this.refreshTokenSubject.next(res?.access || '');
            authService.setStoreRefreshToken(res?.refresh || '');
            authService.setStoreToken(res?.access || '');
            let retorno = new AuthModel();
            retorno.authToken = this.getSessionKey("token") || '';
            this.setAuthFromLocalStorage(retorno);
            this.isRefreshing = false;
            return next.handle(
              req.clone({ setHeaders: { Authorization: `Bearer ${res?.access || ''}` } })
            );
          }),
          catchError((refreshErr) => {
            this.isRefreshing = false;
            //authService.logout();
            sessionStorage.clear();
            localStorage.clear();
            //this.router.navigate(['/auth/login']);
            window.location.href='/auth/login';
            return throwError(() => refreshErr);
          })
        );
      } else {
        this.isRefreshing = false;
        sessionStorage.clear();
        localStorage.clear();
        //this.router.navigate(['/auth/login']);
        window.location.href='/auth/login';
        console.error('No se encontró un token de actualización válido. ');
      }
    }

    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => {
        return next.handle(
          req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
        );
      })
    );
  }

  private getAuthFromLocalStorage(): AuthModel | undefined {
    const  authService:AuthService = this.injector.get(AuthService);
    try {
      const data = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY);
      if (!data) {
        return undefined;
      }

      const decryptedData = this.encryptService.decrypt(data);
      if (!decryptedData) {
        return undefined;
      }
      let retorno = JSON.parse(decryptedData) as AuthModel;
      retorno.authToken = this.getSessionKey("token") || '';
      return retorno;
    } catch (error) {
      localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY);
      sessionStorage.clear();
      console.error('Error al obtener los datos de autenticación:', error);
      return undefined;
    }
  }
}
